/**
 * `next/link` — a React Router <Link> that accepts Next's prop names.
 *
 * `prefetch`, `replace`, `scroll` and `shallow` are accepted so a Next-shaped call site
 * type-checks; only `replace` has a meaning here and it is honoured. An external or
 * protocol-relative href falls through to a plain <a>: handing it to React Router would
 * try to resolve it as an in-app route and produce a 404 in the shell instead of leaving
 * the page.
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface NextLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  replace?: boolean;
  prefetch?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  children?: React.ReactNode;
}

const NextLink = React.forwardRef<HTMLAnchorElement, NextLinkProps>(function NextLink(
  { href, replace, prefetch: _prefetch, scroll: _scroll, shallow: _shallow, children, ...rest },
  ref,
) {
  const isExternal = /^([a-z][a-z0-9+.-]*:)?\/\//i.test(href) || href.startsWith('mailto:');

  if (isExternal) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <RouterLink ref={ref} to={href} replace={replace} {...rest}>
      {children}
    </RouterLink>
  );
});

export default NextLink;
